/**
 * Detects out-of-credits state from stream errors and creditStatus.
 *
 * Two detection paths:
 * 1. 402 error from middleware (blocked before run starts)
 * 2. creditStatus in stream values (ran out during the run)
 *
 * Works inside any Chat.Root — uses useStreamSelector (not page-specific selectors).
 */
import { useEffect, useRef } from "react";
import { useStreamSelector } from "@launch10/use-langgraph-stream";
import { useCreditStore } from "~/stores/creditStore";

export function useOutOfCreditsDetection() {
  // Path 1: 402 error
  const error = useStreamSelector((s) => s.error);

  useEffect(() => {
    if (!error) return;
    const message = error instanceof Error ? error.message : String(error);

    if (!message.includes("CREDITS_EXHAUSTED") && !message.includes("Insufficient credits")) {
      return;
    }

    const store = useCreditStore.getState();
    try {
      const parsed = JSON.parse(message);
      if (parsed.code === "CREDITS_EXHAUSTED") {
        store.updateFromBalanceCheck({
          balanceMillicredits: parsed.balance ?? 0,
          planMillicredits: parsed.planCredits ?? 0,
          packMillicredits: parsed.packCredits ?? 0,
          isExhausted: true,
        });
        store.showModal();
      }
    } catch {
      store.updateFromBalanceCheck({
        balanceMillicredits: 0,
        planMillicredits: 0,
        packMillicredits: 0,
        isExhausted: true,
      });
      store.showModal();
    }
  }, [error]);

  // Path 2: creditStatus in stream values
  // Cast values to any — this hook is generic across all graph states,
  // but all our graphs include creditStatus in their state type.
  const justExhausted = useStreamSelector(
    (s) => (s.values as any)?.creditStatus?.justExhausted as boolean | undefined
  );
  const estimatedRemaining = useStreamSelector(
    (s) => (s.values as any)?.creditStatus?.estimatedRemainingMillicredits as number | undefined
  );

  // Track whether a live stream has started. History loads populate values
  // (including stale creditStatus) while isLoading is false. We only want to
  // process creditStatus from a real stream run, not from thread history.
  const isLoading = useStreamSelector((s) => s.isLoading);
  const hasStreamedRef = useRef(false);

  useEffect(() => {
    if (isLoading) hasStreamedRef.current = true;
  }, [isLoading]);

  useEffect(() => {
    if (estimatedRemaining === undefined) return;
    if (!hasStreamedRef.current) return;

    useCreditStore.getState().updateFromCreditStatus({
      estimatedRemainingMillicredits: estimatedRemaining,
      justExhausted: justExhausted ?? false,
    });
  }, [justExhausted, estimatedRemaining]);
}
